import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAttendance } from "./attendanceSlice";
import styles from "./Attendance.module.scss";
import {
  breakTypes,
  formatElapsedDetailed,
} from "../../constants/common.constants";
import { broadcastActivity } from '../../utils/activityBroadcast';

export function Attendance() {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const isFirstRender = useRef(true);
  const location = useLocation();

  // Load selectedStatus from localStorage if available
  const getInitialSelectedStatus = () =>
    localStorage.getItem("selectedStatus") || null;
  const [selectedStatus, setSelectedStatus] = useState(
    getInitialSelectedStatus()
  );
  const [hasStarted, setHasStarted] = useState(false);
  const [prevStatus, setPrevStatus] = useState(() => 
    localStorage.getItem("selectedStatus") || null
  );
  const [elapsed, setElapsed] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRecentLoggedIn, setIsRecentLoggedIn] = useState(false);
  const [isPageReload, setIsPageReload] = useState(true);

  const user = useSelector((state) => state.authentication.user);

  // Determine if user is in moderation mode and should have locked attendance status
  const isOnProjectPage = /^\/projects\/\d+/.test(location.pathname);
  const isModeratorUser = user && !user.is_admin_or_qa;
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === 'true';
  const shouldLockToModerating = isOnProjectPage && isModeratorUser && !isPreviewMode;
  useEffect(() => { 
    if (!user) {
      localStorage.removeItem("selectedStatus");
    }
    // Set selectedStatus to "offline" after login if user just logged in
    if (user && !localStorage.getItem("selectedStatus")) {
      setIsRecentLoggedIn(true);
      setSelectedStatus("offline");
    }
  }, [user]);

  // Auto-set status to "moderating" when moderator enters project page
  useEffect(() => {
    if (shouldLockToModerating && selectedStatus !== "moderating") {
      setSelectedStatus("moderating");
    }
  }, [shouldLockToModerating, selectedStatus]);

  const breakLogs = useSelector((state) => state.attendance?.breakLogs ?? null);

  // Save selectedStatus to localStorage on change
  useEffect(() => {
    if (selectedStatus) {
      localStorage.setItem("selectedStatus", selectedStatus);
    } else {
      localStorage.removeItem("selectedStatus");
    }
  }, [selectedStatus]);

  // Save breakLogs.start_time to localStorage whenever it changes
  useEffect(() => {
    if (breakLogs && breakLogs.start_time) {
      localStorage.setItem("breakLogsStartTime", breakLogs.start_time);
    }
    // else {
    //   localStorage.removeItem("breakLogsStartTime");
    // }
  }, [breakLogs]);

  // Use breakLogs.start_time from localStorage for timer
  const getStartTime = useCallback(() => {
    const storedTime = localStorage.getItem("breakLogsStartTime");
    if (storedTime) {
      return storedTime;
    }
    if (breakLogs && breakLogs.start_time) {
      return breakLogs.start_time;
    }
    return null;
  }, [breakLogs]);

  // Helper functions for UI
  const getAvailableBreakTypes = () => {
    // If moderator is locked to moderating status (on project page), only show "moderating"
    if (shouldLockToModerating) {
      return breakTypes.filter((type) => type.key === "moderating");
    }

    // Filter out "moderating" status based on user role
    // Only regular moderators (non-admin, non-QA users) can access "Moderating" status
    // This maintains strict role separation: admins manage, QA reviews, moderators moderate
    if (!user) {
      return breakTypes.filter((type) => type.key !== "moderating");
    }

    // Allow "Moderating" status only for regular moderators
    // Block both admin and QA users from moderating to maintain role separation
    const canModerate = !user.is_admin_or_qa;

    if (!canModerate) {
      return breakTypes.filter((type) => type.key !== "moderating");
    }

    return breakTypes;
  };

  const getCurrentStatus = () => {
    if (selectedStatus) {
      const selected = breakTypes.find((b) => b.key === selectedStatus);
      return selected || { label: "Unknown", color: "#a6a6a6" };
    }
    return { label: "Select Status", color: "#a6a6a6" };
  };

  const toggleDropdown = () => {
    // Prevent dropdown from opening when status is locked to moderating
    if (shouldLockToModerating) {
      return;
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleStatusChange = (statusKey) => {
    setSelectedStatus(statusKey);
    setIsDropdownOpen(false);
    
    // Update start time immediately for new status
    if (statusKey && statusKey !== "offline") {
      const now = new Date().toISOString();
      localStorage.setItem("breakLogsStartTime", now);
    } else if (statusKey === "offline") {
      localStorage.removeItem("breakLogsStartTime");
    }
    
    broadcastActivity();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const currentStatus = useMemo(() => getCurrentStatus(), [selectedStatus]);

  useEffect(() => {
    // Skip effect on first render (page reload)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setIsPageReload(false);
      return;
    }
    if (isRecentLoggedIn) {
      setIsRecentLoggedIn(false);
      return;
    }

    // Only create break log if status actually changed (not on page reload)
    if (selectedStatus && selectedStatus !== prevStatus && !isPageReload) {
      // Stop previous status if there was one
      if (hasStarted && prevStatus) {
        dispatch(
          setAttendance({
            type: prevStatus,
            label: "stop",
            userId: user.id,
          })
        );
        setHasStarted(false);
      }
      
      const timeoutId = setTimeout(() => {
        dispatch(
          setAttendance({
            type: selectedStatus,
            label: "start",
            userId: user.id,
          })
        );
        setPrevStatus(selectedStatus);
        setHasStarted(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedStatus, prevStatus, hasStarted, isPageReload, dispatch, user.id]);

  useEffect(() => {
    let timerId;
    const startTime = getStartTime();
    
    // Disable timer if selectedStatus is "offline"
    if (selectedStatus === "offline") {
      setElapsed(0);
      return () => { };
    }
    
    if (startTime) {
      const start = new Date(startTime).getTime();
      // Calculate initial elapsed time immediately
      const initialElapsed = Math.floor((Date.now() - start) / 1000);
      setElapsed(initialElapsed);
      
      timerId = setInterval(() => {
        setElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    } else {
      setElapsed(0);
    }
    
    return () => clearInterval(timerId);
  }, [selectedStatus, getStartTime]);

  return (
    <div className={styles["user-status-indicator"]} ref={dropdownRef}>
      <button
        className={`${styles["status-dropdown-trigger"]} ${shouldLockToModerating ? styles["status-locked"] : ""}`}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
        aria-label={shouldLockToModerating
          ? `Status locked to ${currentStatus.label} during moderation session.`
          : `Current status: ${currentStatus.label}. Click to change status.`}
        title={shouldLockToModerating
          ? "Status is locked to Moderating while in moderation session"
          : "Click to change status"}
      >
        <div className={styles["status-info-left"]}>
          <div
            className={styles["status-circle"]}
            style={{ backgroundColor: currentStatus.color }}
          ></div>
          <span className={styles["status-text"]}>{currentStatus.label}</span>
        </div>
        <div className={styles["status-info-right"]}>
          {(breakLogs?.start_time ||
            localStorage.getItem("breakLogsStartTime")) && (
              <div className={styles["timer-display"]}>
                {formatElapsedDetailed(elapsed)}
              </div>
            )}
          <span
            className={`${styles["dropdown-arrow"]} ${isDropdownOpen ? styles["dropdown-arrow-open"] : ""} ${shouldLockToModerating ? styles["dropdown-arrow-locked"] : ""}`}
          >
            {shouldLockToModerating ? "🔒" : "▼"}
          </span>
        </div>
      </button>

      {isDropdownOpen && (
        <div className={styles["dropdown-menu"]} role="menu">
          {getAvailableBreakTypes().map((type) => (
            <button
              key={type.key}
              role="menuitem"
              className={styles["dropdown-item"]}
              onClick={() => handleStatusChange(type.key)}
            >
              <div
                className={styles["item-circle"]}
                style={{ backgroundColor: type.color }}
              ></div>
              <span className={styles["item-text"]}>{type.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
